const THEME_MODES = new Set(['light', 'dark', 'auto']);
let sessionLocation = null;

export function normalizeThemeMode(profile) {
  if (THEME_MODES.has(profile?.map_theme)) return profile.map_theme;
  if (profile?.map_dark_mode === true) return 'dark';
  if (profile?.map_dark_mode === false) return 'light';
  return 'auto';
}

function solarState(latitude, longitude, now = new Date()) {
  const day = Math.floor((Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) - Date.UTC(now.getUTCFullYear(), 0, 0)) / 86400000);
  const declination = 23.44 * Math.sin(((360 / 365) * (day - 81) * Math.PI) / 180);
  const latitudeRad = (latitude * Math.PI) / 180;
  const declinationRad = (declination * Math.PI) / 180;
  const cosHourAngle = (Math.cos((90.833 * Math.PI) / 180) / (Math.cos(latitudeRad) * Math.cos(declinationRad))) - Math.tan(latitudeRad) * Math.tan(declinationRad);
  if (cosHourAngle >= 1) return 'dark';
  if (cosHourAngle <= -1) return 'light';
  const hourAngle = (Math.acos(cosHourAngle) * 180) / Math.PI;
  const solarNoonUtc = 720 - 4 * longitude;
  const sunriseUtc = solarNoonUtc - 4 * hourAngle;
  const sunsetUtc = solarNoonUtc + 4 * hourAngle;
  const minutes = now.getUTCHours() * 60 + now.getUTCMinutes() + now.getUTCSeconds() / 60;
  return minutes >= sunriseUtc && minutes < sunsetUtc ? 'light' : 'dark';
}

export function getCachedLocation() {
  return sessionLocation;
}

export function cacheLocation(position) {
  const location = { lat: position.coords.latitude, lng: position.coords.longitude };
  sessionLocation = location;
  return location;
}

export function resolveTheme(mode, location, now = new Date()) {
  if (mode !== 'auto') return mode;
  // iOS PWAs may deny or delay geolocation. Keep Auto useful in that case by
  // following the device's local clock until coordinates become available.
  if (!location) {
    const hour = now.getHours();
    return hour >= 7 && hour < 19 ? 'light' : 'dark';
  }
  return solarState(location.lat, location.lng, now);
}

export function nextThemeBoundary(location, now = new Date()) {
  if (!location) return 60 * 60 * 1000;
  for (let minutes = 1; minutes <= 24 * 60; minutes += 1) {
    const candidate = new Date(now.getTime() + minutes * 60 * 1000);
    if (resolveTheme('auto', location, candidate) !== resolveTheme('auto', location, now)) return candidate.getTime() - now.getTime();
  }
  return 60 * 60 * 1000;
}

export async function requestThemeLocation() {
  if (!navigator.geolocation) return getCachedLocation();
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(cacheLocation(position)),
      () => resolve(getCachedLocation()),
      { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 8000 }
    );
  });
}

export function watchThemeLocation(onLocation) {
  if (!navigator.geolocation || typeof onLocation !== 'function') return () => {};
  const watchId = navigator.geolocation.watchPosition(
    (position) => onLocation(cacheLocation(position)),
    () => {},
    { enableHighAccuracy: false, maximumAge: 5 * 60 * 1000, timeout: 30000 }
  );
  return () => navigator.geolocation.clearWatch(watchId);
}
